import { Box } from "@mui/material";
import { FC, PropsWithChildren, ReactNode } from "react";

type Props = PropsWithChildren<{
  /** Omit for a plain frame — the border is then drawn unbroken. */
  label?: string;
  error?: boolean;
  /**
   * Rendered inside the frame, which is positioned — pass something absolutely
   * placed to hang it on the border opposite the heading.
   */
  action?: ReactNode;
}>;

/**
 * A titled frame whose heading sits in a gap in its own border, matching the
 * floating label on a filled outlined input.
 *
 * Built on a real fieldset/legend, the same mechanism MUI's outlined inputs use:
 * the browser breaks the border for the legend itself, so the heading needs no
 * background swatch tracking whatever colour sits behind it.
 */
export const OutlinedSection: FC<Props> = ({
  label,
  error,
  action,
  children,
}) => (
  <Box
    component="fieldset"
    sx={{
      position: "relative",
      margin: 0,
      padding: "8px",
      border: 1,
      borderColor: error ? "error.main" : "divider",
      borderRadius: 1,
      // Fieldsets default to `min-width: min-content`, which would stop a column
      // shrinking inside a flex row.
      minWidth: 0,
      // Fills a stretched parent so frames in the same row match; resolves to
      // `auto` when the parent is content-height, so it costs nothing elsewhere.
      height: "100%",
    }}
  >
    {label && (
      <Box
        component="legend"
        sx={{
          // `auto` inline margins are what centre a legend.
          marginInline: "auto",
          padding: "0 4px",
          fontSize: 9,
          // Pins the line box to the glyphs, so the text sits centred on the
          // border rather than high inside a taller default line box — which is
          // what anything else hung on the border has to line up with.
          lineHeight: 1,
          color: error ? "error.main" : "text.secondary",
        }}
      >
        {label}
      </Box>
    )}
    {action}
    {children}
  </Box>
);
